/**
 * Writing the language that has not been written yet.
 *
 * ⚠️ THIS EXISTS BECAUSE A SITE BUILT WITHOUT AN ACCOUNT IS WRITTEN IN ONE LANGUAGE (CLAUDE.md
 * §5.15). That halves what the most expensive request in the product costs and takes about a third
 * off the wait — spent on the visitor who is deciding whether to type anything at all — and the debt
 * it takes on is settled here: when they switch language, and again before they publish.
 *
 * ⚠️ IT FILLS ONLY THE BLANKS **WE** LEFT, AND THAT IS THE WHOLE RULE FOR WHEN IT MAY RUN BY ITSELF.
 * On a site that lives in the browser, every missing string is one the generator was told not to
 * write; filling those in is finishing our own job, and asking permission for it would be asking
 * somebody to authorise a repair to something they did not know was broken. On a site with an owner,
 * a blank is a decision somebody may have made — so it is offered, in a line of text with a button,
 * and nothing is spent until they press it.
 *
 * ⚠️ AND IT NEVER TOUCHES COPY THAT MERELY LAGS. `textGaps` reports blanks, not `stale` (§2.5): the
 * drawer already offers to bring a lagging translation up to date, one field at a time, where the
 * customer can see both. Sweeping those up in a bulk pass would silently overwrite copy they wrote.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Notice, Spinner, useLang } from '@ksa/ui'
import { applyGap, textGaps, type Gap, type Lang, type Site } from '@ksa/model'
import { api, ApiFailure } from './api.ts'

type Phase =
  | { at: 'idle' }
  | { at: 'working'; count: number }
  | { at: 'failed'; failure: ApiFailure | null }
  /** Some ids came back and some did not — see `translateMany`, which drops what it cannot match. */
  | { at: 'short'; written: number; asked: number }

export function LanguageFill({
  site,
  lang,
  auto,
  siteName,
  onApply,
}: {
  site: Site
  /** The language being previewed — the one that has to be there. */
  lang: Lang
  /** Whether this document's blanks are ours to fill without being asked. See the note above. */
  auto: boolean
  siteName: string
  onApply: (fold: (current: Site) => Site) => void
}) {
  const { t, r } = useLang()
  const [phase, setPhase] = useState<Phase>({ at: 'idle' })

  /*
   * ⚠️ MEMOISED, BECAUSE THIS WALKS THE WHOLE DOCUMENT AND THE EDITOR RE-RENDERS ON EVERY KEYSTROKE.
   * It is a few hundred fields at worst, so the cost is small — but it is a walk per character typed
   * in a text box, which is the shape of thing that is fine until somebody writes an about page.
   */
  const gaps = useMemo(() => textGaps(site, lang, lang), [site, lang])

  /*
   * ⚠️ THE LIVE DOCUMENT AND THE LIVE GAPS, READ AT THE MOMENT THE REQUEST IS SENT. A `useCallback`
   * closing over `gaps` would be rebuilt on every keystroke, and the effect below would re-fire with
   * whichever list happened to be captured when it mounted.
   */
  const latest = useRef<Gap[]>(gaps)
  latest.current = gaps

  const fill = useCallback(async () => {
    const wanted = latest.current
    if (wanted.length === 0) return
    setPhase({ at: 'working', count: wanted.length })

    /*
     * ⚠️ THE ID IS THE INDEX IN THIS LIST AND NOTHING ELSE — it never leaves this function, and it is
     * how the reply is matched back to a place in the document. The model is handed a label and a
     * string; it is not handed a block id, a page id or anything else it could be persuaded to
     * repeat back somewhere it matters.
     */
    try {
      const texts = await api.translateMany({
        to: lang,
        siteName,
        items: wanted.map((gap, index) => ({ id: String(index), label: gap.label, source: gap.source })),
      })

      const written = Object.keys(texts).length
      onApply((current) =>
        wanted.reduce((next, gap, index) => {
          const text = texts[String(index)]
          return text ? applyGap(next, gap.at, lang, text) : next
        }, current),
      )
      setPhase(written < wanted.length ? { at: 'short', written, asked: wanted.length } : { at: 'idle' })
    } catch (error) {
      setPhase({ at: 'failed', failure: error instanceof ApiFailure ? error : null })
    }
  }, [lang, onApply, siteName])

  /**
   * ⚠️ ONCE PER LANGUAGE, AND THE GUARD IS A REF RATHER THAN A PIECE OF STATE. Filling the gaps
   * changes the document, which re-renders this component with a shorter list — and anything the
   * model declined to translate is still a gap, so an effect that only looked at the count would ask
   * again, and again, for as long as the tab was open. Each attempt is a model call.
   */
  const attempted = useRef<Set<string>>(new Set())
  useEffect(() => {
    if (!auto || gaps.length === 0 || phase.at === 'working') return
    if (attempted.current.has(lang)) return
    attempted.current.add(lang)
    void fill()
  }, [auto, gaps.length, lang, phase.at, fill])

  if (phase.at === 'working') {
    const language = lang === 'ar' ? t('Arabic', 'العربية') : t('English', 'الإنجليزية')
    return (
      <div className="ed__veil" role="status" aria-live="polite">
        <div className="ed__veil__backdrop" />
        <div className="ed__hello ed__hello--tight">
          <Spinner label={t('Translating', 'جارٍ الترجمة')} />
          <h2 className="u-title">{t(`Writing the ${language}`, `جارٍ كتابة ${language}`)}</h2>
          {/*
            ⚠️ THE COUNT IS THE PROGRESS, AND THERE IS NO BAR. It is one request; a bar that filled
            itself on a timer would be an animation pretending to be a measurement, which is the sort
            of on-screen claim §0.4 is about. The number of things being written is true, and it is
            what tells somebody whether this is a moment or a minute.
          */}
          <p className="u-small u-faint">
            {t(
              `${phase.count} things on this site are only in the other language. This takes a few seconds.`,
              `${phase.count} عنصرًا في هذا الموقع مكتوب باللغة الأخرى فقط. يستغرق هذا بضع ثوانٍ.`,
            )}
          </p>
        </div>
      </div>
    )
  }

  if (gaps.length === 0 && phase.at !== 'failed' && phase.at !== 'short') return null

  return (
    <div className="ed__fill">
      {phase.at === 'failed' ? (
        <Notice tone="warn">
          {phase.failure
            ? r(phase.failure.text)
            : t('That translation did not come back.', 'لم تُكمل الترجمة.')}
        </Notice>
      ) : (
        <p className="u-small">
          {phase.at === 'short'
            ? t(
                `${phase.written} of ${phase.asked} were written. The rest are still missing.`,
                `كُتب ${phase.written} من ${phase.asked}. الباقي ما زال ناقصًا.`,
              )
            : t(
                `${gaps.length} things on this site have no ${lang === 'ar' ? 'Arabic' : 'English'} yet.`,
                `${gaps.length} عنصرًا في هذا الموقع بلا ${lang === 'ar' ? 'عربية' : 'إنجليزية'} بعد.`,
              )}
        </p>
      )}
      <Button size="sm" variant="quiet" onClick={() => void fill()}>
        {t('Write them', 'اكتبها')}
      </Button>
    </div>
  )
}
